import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../AuthContext";
import {
  canUseContentManagerProfileFallback,
  hasContentManagerAccess,
  hasContentManagerProfileRole,
  resolvePreferredContentManagerRole,
} from "../admin/adminAccess";
import {
  hasLostProtectedEntitlement,
  removeEntitlementSensitiveQueries,
} from "../utils/entitlementQueryCache";

const UserContext = createContext();

const USER_PROFILE_QUERY_KEY = ["user", "profile"];
const USER_ADMIN_STATUS_QUERY_KEY = ["user", "admin-status"];

export const UserProvider = ({ children }) => {
  // Updated destructuring – note that token and refreshToken are removed.
  const { isAuthenticated, logout, user: authUser, api } = useAuth();
  const queryClient = useQueryClient();
  const previousAccessRef = useRef({ userId: null, profile: null });
  const wasAuthenticatedRef = useRef(Boolean(isAuthenticated));

  const syncUser = useCallback(async () => {
    if (!authUser) return;

    const preferredContentManagerRole =
      resolvePreferredContentManagerRole(authUser);
    const requestBody = {
      username: authUser.username || authUser.email || "Unnamed",
      externalId: authUser.sub,
    };

    if (authUser.provider === "vipps") {
      requestBody.vippsUserId = authUser?.sub;
    }

    if (preferredContentManagerRole) {
      requestBody.role = preferredContentManagerRole;
    }

    try {
      await api.post(`/bruker/sync-user`, requestBody);
    } catch (err) {
      console.error("Error syncing user:", err);
      logout();
      throw err;
    }
  }, [api, authUser, logout]);

  const fetchUserProfile = useCallback(async () => {
    const { data } = await api.get(`/bruker/profile`);
    return data;
  }, [api]);

  // Fetch the profile, syncing the user first when needed: fast path for
  // returning users avoids sync-user when the profile already exists, and a
  // missing content-manager role triggers a sync + refetch.
  const fetchProfileWithSync = useCallback(async () => {
    let fetchedUserData;
    try {
      fetchedUserData = await fetchUserProfile();
    } catch (err) {
      if (err?.response?.status !== 404) {
        throw err;
      }
      await syncUser();
      fetchedUserData = await fetchUserProfile();
    }

    const preferredContentManagerRole =
      resolvePreferredContentManagerRole(authUser);
    if (
      preferredContentManagerRole &&
      !hasContentManagerProfileRole(fetchedUserData.role)
    ) {
      await syncUser();
      fetchedUserData = await fetchUserProfile();
    }

    return fetchedUserData;
  }, [authUser, fetchUserProfile, syncUser]);

  const profileQueryKey = useMemo(
    () => [...USER_PROFILE_QUERY_KEY, authUser?.sub ?? null],
    [authUser?.sub],
  );

  const profileQuery = useQuery({
    queryKey: profileQueryKey,
    queryFn: async () => {
      try {
        return await fetchProfileWithSync();
      } catch (err) {
        console.error("Error during synchronization and fetching:", err);
        throw err;
      }
    },
    enabled: isAuthenticated,
    // Fetched once per session/user; refreshUserData forces a reload.
    staleTime: Infinity,
    retry: false,
  });
  const userData = (isAuthenticated ? profileQuery.data : null) ?? null;
  const adminStatusQuery = useQuery({
    queryKey: [...USER_ADMIN_STATUS_QUERY_KEY, authUser?.sub ?? null],
    queryFn: async () => {
      const { data } = await api.get("/bruker/is-admin");
      return data?.isAdmin === true;
    },
    enabled: isAuthenticated && Boolean(userData),
    staleTime: Infinity,
    retry: false,
  });
  const { refetch: refetchAdminStatus } = adminStatusQuery;
  const isAdmin = adminStatusQuery.data === true;
  const isContentManager =
    isAdmin ||
    (userData ? hasContentManagerAccess({ authUser, userData }) : false);
  const claimedContentManagerRole =
    resolvePreferredContentManagerRole(authUser);
  const userRole = userData
    ? claimedContentManagerRole ||
      (canUseContentManagerProfileFallback(authUser) ? userData.role : null)
    : null;
  const activeCourse = userData?.activeCourse || null;
  const loading = isAuthenticated
    ? profileQuery.isFetching ||
      (Boolean(userData) && adminStatusQuery.isFetching)
    : false;
  const error = isAuthenticated
    ? profileQuery.error || adminStatusQuery.error || null
    : null;

  // Protected responses may contain worked solutions or premium training data.
  // Purge them before publishing a downgraded profile, so a Free session cannot
  // inherit paid data from the same browser's React Query cache.
  const refreshUserData = useCallback(async () => {
    const previousProfile = queryClient.getQueryData(profileQueryKey);
    const nextProfile = await fetchProfileWithSync();
    if (hasLostProtectedEntitlement(previousProfile, nextProfile)) {
      removeEntitlementSensitiveQueries(queryClient);
    }
    queryClient.setQueryData(profileQueryKey, nextProfile);
    await refetchAdminStatus();
    return nextProfile;
  }, [fetchProfileWithSync, profileQueryKey, queryClient, refetchAdminStatus]);

  // Also cover logout, direct identity changes, and any profile update that did
  // not go through refreshUserData.
  useEffect(() => {
    const currentUserId = isAuthenticated ? authUser?.sub || null : null;
    const previous = previousAccessRef.current;

    if (!isAuthenticated) {
      if (wasAuthenticatedRef.current) {
        removeEntitlementSensitiveQueries(queryClient);
      }
      queryClient.removeQueries({ queryKey: USER_PROFILE_QUERY_KEY });
      queryClient.removeQueries({ queryKey: USER_ADMIN_STATUS_QUERY_KEY });
      previousAccessRef.current = { userId: null, profile: null };
      wasAuthenticatedRef.current = false;
      return;
    }

    wasAuthenticatedRef.current = true;
    if (
      (previous.userId && previous.userId !== currentUserId) ||
      (previous.userId === currentUserId &&
        previous.profile &&
        userData &&
        hasLostProtectedEntitlement(previous.profile, userData))
    ) {
      removeEntitlementSensitiveQueries(queryClient);
    }

    if (userData) {
      previousAccessRef.current = {
        userId: currentUserId,
        profile: userData,
      };
    }
  }, [authUser?.sub, isAuthenticated, queryClient, userData]);

  const updateActiveCourse = async (course) => {
    try {
      await api.post(`/bruker/update-active-course`, { activeCourse: course });
      queryClient.setQueryData(profileQueryKey, (prev) => ({
        ...prev,
        activeCourse: course,
      }));
    } catch (err) {
      console.error("Error updating active course:", err);
      throw err;
    }
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        isAdmin,
        isContentManager,
        userRole,
        activeCourse,
        loading,
        error,
        refreshUserData,
        updateActiveCourse,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserValues = () => useContext(UserContext);
